import React from "react";
import {find as _find} from 'lodash';
import {Button, ButtonSwitch, ButtonSwitchOption, Icon} from '@gisatcz/ptr-atoms';
import PropTypes from "prop-types";

import vectorLayers from '../../data/layers/vectorLayers';
import configuration from '../../data/configuration';
import {Intervals, IntervalsLine, SectionHeader, ValuesByColor, Symbol} from '../Legend';
import styles from '../../data/styles/styles';

import "./style.scss";

class LayerControl extends React.PureComponent {
	static propTypes = {
		activeMap: PropTypes.object,
		layerKey: PropTypes.string
	}

	constructor(props) {
		super(props);
	}

	handleVectorLayer(layerKey, styleKey) {
		const props = this.props;

		const mapKey = props.activeMap.key;
		const smallMapKey = props.pairConfig.maps[mapKey];
		const smallLayerKey = props.pairConfig.layers[layerKey];
		const smallStyleKey = props.pairConfig.styles[styleKey];

		const layerData = _find(vectorLayers, layer => layer.key === layerKey);
		const smallLayerData = _find(vectorLayers, layer => layer.key === smallLayerKey);

		const currentLayers = props.activeMap?.data?.layers || [];
		const existingLayer = _find(currentLayers, (layer) => layer.key === layerKey);

		if (styleKey) {
			if (!existingLayer) {
				const layerOrder = configuration.layerOrder[mapKey];
				const index = this.getLayerIndex(layerKey, layerOrder, currentLayers.map(layer => layer.key));

				this.props.addVectorLayer(mapKey, layerData, index);
				this.props.addVectorLayer(smallMapKey, smallLayerData, index);
			}

			const styleDefinition = this.props.styles[styleKey]?.data.definition;
			const smallStyleDefinition = this.props.styles[smallStyleKey]?.data.definition

			this.props.setLayerStyle(mapKey, layerKey, styleDefinition);
			this.props.setLayerStyle(smallMapKey, smallLayerKey, smallStyleDefinition);
		} else {
			this.props.removeVectorLayer(mapKey, layerKey);
			this.props.removeVectorLayer(smallMapKey, smallLayerKey);
		}

		this.props.changeActiveLayerStyle(layerKey, styleKey);
	}

	getLayerIndex(key, order, currentList) {
		const keyIndexInOrder = order.indexOf(key);

		let i = 0;
		for (i; i < currentList.length; i++) {
			let item = currentList[i];
			let itemIndexInOrder = order.indexOf(item);

			if (itemIndexInOrder > keyIndexInOrder) {
				break;
			}
		}

		return i;
	}

	render() {
		return (
			<div className="eo4sdDrrInsarMyanmar20-layer-control-wrapper">
				{this.renderVectorLayerSwitch(this.props.layerKey)}
				{this.renderVectorLayerLegend(this.props.layerKey)}
			</div>
		);
	}

	renderVectorLayerSwitch(layerKey) {
		const props = this.props;
		const availableStyleKeys = props.layerStylesConfig?.[layerKey]?.available;
		const availableStyles = availableStyleKeys?.map(key => props.styles[key]);
		const activeStyleKey = props.layerStylesConfig?.[layerKey]?.active;

		const content = availableStyles.map(style =>
			<ButtonSwitchOption
				value={style.key}
				active={style.key === activeStyleKey}
				small={this.props.small}
			>
				{style.data?.nameDisplay}
			</ButtonSwitchOption>
		);

		if (!props?.hideRemoval) {
			content.push(
				<ButtonSwitchOption
					value={null}
					active={!activeStyleKey}
				>
					None
				</ButtonSwitchOption>
			);
		}


		return (
			<ButtonSwitch onClick={this.handleVectorLayer.bind(this, layerKey)} ghost>
				{content}
			</ButtonSwitch>
		);
	}

	renderVectorLayerLegend(layerKey) {
		const activeStyleKey = this.props.layerStylesConfig?.[layerKey]?.active;

		if (activeStyleKey) {
			let content = null;

			if (activeStyleKey === "riskAssessmentZonesP75Big" || activeStyleKey === "riskAssessmentZonesP75DtsBig") {
				const classes = styles.p75AttributeIntervalsZones.attributeClasses;
				const noDataColor = styles.zoneBase.fill;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Annual displacement rate [mm/year]" subtitle="From zonal 75-percentile in LOS"/>
						<IntervalsLine styleProperty="fill" classes={classes} noData={noDataColor} longerTails="last"/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentZonesSusceptibilityBig") {
				const classes = styles.susceptibility.attributeClasses;
				const noDataColor = styles.zoneBase.fill;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Landslide susceptibility (75-percentile)"/>
						<IntervalsLine styleProperty="fill" classes={classes} noData={noDataColor}/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentZonesLandUseBig") {
				const items = styles.landUseClassification.attributeValues;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Land use classes"/>
						<ValuesByColor data={items}/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentZonesChangeYearBig") {
				const items = styles.chngYearAttributeValues.attributeValues;
				const noDataColor = styles.zoneBase.fill;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Year of built-up change"/>
						<ValuesByColor data={items} noData={noDataColor}/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentZonesLandslidesBig") {
				const items = styles.landslides.attributeValues;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Number of detected landslides"/>
						<ValuesByColor data={items}/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentZonesChangeTypeBig") {
				let items = {...styles.chngTypeAttributeValues.attributeValues, ...styles.chngTypeLandUseAttributeValues.attributeValues};
				const noDataColor = styles.zoneBase.fill;

				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Built-up change type"/>
						<ValuesByColor data={items} noData={noDataColor}/>
					</div>
				);
			}

			// Roads
			else if (activeStyleKey === "riskAssessmentRoadsP75Big") {
				const classes = styles.p75AttributeIntervalsRoads.attributeClasses;
				const noDataColor = styles.roadBase.outlineColor;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Annual displacement rate [mm/year]" subtitle="From weighted zonal 75-percentile in LOS"/>
						<IntervalsLine styleProperty="outlineColor" classes={classes} noData={noDataColor} longerTails="last"/>
					</div>
				);
			} else if (activeStyleKey === "riskAssessmentRoadsTypologyBig") {
				const classes = styles.roadTypology.attributeClasses;
				const baseColor = styles.roadBase.outlineColor;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Type"/>
						<Intervals styleProperty="outlineWidth" classes={classes} baseColor={baseColor}/>
					</div>
				);
			}

			// Points
			else if (activeStyleKey === "interferometryPointsVelAvgBig") {
				const classes = styles.velAvg.attributeClasses;
				const noDataColor = styles.interferometryBase.fill;
				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Annual displacement rate [mm/year]"/>
						<IntervalsLine styleProperty="fill" classes={classes} noData={noDataColor} longerTails/>
					</div>
				);
			} else if (activeStyleKey === "interferometryPointsSpatialPatternTypeBig") {
				let items = styles.spatialPatternType.attributeValues;
				const noDataColor = styles.zoneBase.fill;

				content = (
					<div className="eo4sdDrrInsarMyanmar20-legend-section">
						<SectionHeader title="Type of spatial pattern: cluster / outlier"/>
						<ValuesByColor data={items} noData={noDataColor} noDataTitle="Not significant clustering"/>
					</div>
				);
			} else if (activeStyleKey === "interferometryPointsVelAvgAlarmBig") {
				const classes = styles.velAvg.attributeClasses;
				const noDataColor = styles.interferometryBase.fill;

				const alarmStyle = {...styles.interferometryBase, ...styles.alarms.attributeValues[10]};

				content = (
					<>
						<div className="eo4sdDrrInsarMyanmar20-legend-section">
							<SectionHeader title="Annual displacement rate [mm/year]"/>
							<IntervalsLine styleProperty="fill" classes={classes} noData={noDataColor} longerTails/>
						</div>
						<div className="eo4sdDrrInsarMyanmar20-legend-section">
							<SectionHeader title="Alarm"/>
							<Symbol style={alarmStyle}/>
						</div>
					</>
				);
			}

			return (
				<div className="eo4sdDrrInsarMyanmar20-vector-layer-control-legend">
					{content}
				</div>
			);
		} else {
			return null;
		}
	}
}

export default LayerControl;